/**
 * First-time welcome walkthrough.
 *
 * - Auto-opens on first dashboard visit if the user has no `onboarded_at`.
 * - Four short, skippable steps. Final step CTA routes to /campaign-intake.
 * - Marks the user `onboarded_at` on either Skip or Finish — so it doesn't
 *   re-appear on every login.
 * - "Take the tour again" is purely client-side (see <OnboardingReplayButton />)
 *   so re-running it doesn't reset the server-side flag — meaning if the user
 *   takes the tour, dismisses it, then opens it manually a week later, we
 *   don't suddenly start auto-popping it for them again.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  ArrowRight,
  ArrowLeft,
  ClipboardCheck,
  Bot,
  Megaphone,
  Database,
  ClipboardList,
  Users as UsersIcon,
  Wallet,
  Sparkles,
} from "lucide-react";

const ROUTES_WHERE_AUTO_OPENS = new Set(["/dashboard", "/"]);

function OnboardingDialog({ open, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  // Reset to first step every time the dialog opens fresh.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const totalSteps = 4;

  const stepBody = (() => {
    if (step === 0) {
      return {
        eyebrow: "Welcome to Electable",
        title: "You don't have to do this alone.",
        body: (
          <>
            <p>You're at the helm of a real campaign — and that's a lot. Electable is built to make the lonely parts easier: drafting, planning, strategy, the field operation, the books.</p>
            <p className="mt-3">Two minutes here, and you'll know exactly where to start.</p>
          </>
        ),
      };
    }
    if (step === 1) {
      return {
        eyebrow: "Step 1 — The most important thing",
        title: "Complete your full campaign profile first.",
        body: (
          <>
            <p>
              Every post we draft, every speech we write, every piece of strategy from the Advisor — all of it gets sharper the second you fill out your campaign profile.
            </p>
            <p className="mt-3">
              <strong className="text-flag-red">This isn't optional housekeeping — it's the single biggest lever you have.</strong>{" "}
              The intake takes about 10 minutes. It asks about your race, your opponent, your priorities, and your tone of voice. After that, everything else in here knows what kind of campaign it's helping you run.
            </p>
            <div className="mt-4 rounded-lg bg-flag-red/[0.06] border border-flag-red/20 px-4 py-3 text-sm text-flag-navy">
              <strong>Why we're being this insistent:</strong> a generic AI post about &ldquo;property taxes&rdquo; is fine. A post about <em>your</em> opponent&rsquo;s exact tax votes, in <em>your</em> tone, for <em>your</em> district — that's the one that wins.
            </div>
          </>
        ),
      };
    }
    if (step === 2) {
      return {
        eyebrow: "Step 2 — What's in here",
        title: "Six categories, one home screen.",
        body: (
          <>
            <p className="mb-3">After login, the home screen is a grid of six cards. Quick tour:</p>
            <ul className="space-y-2.5 text-[15px]">
              <li className="flex gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 rounded-md bg-flag-red text-white items-center justify-center"><Bot className="h-4 w-4" /></span>
                <div><strong className="text-flag-navy">Advisor</strong> — AI strategy on call. Grounded in your saved profile.</div>
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 rounded-md bg-flag-navy text-white items-center justify-center"><Megaphone className="h-4 w-4" /></span>
                <div><strong className="text-flag-navy">Communications</strong> — Social posts, speeches, scheduling.</div>
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 rounded-md bg-flag-navy text-white items-center justify-center"><Database className="h-4 w-4" /></span>
                <div><strong className="text-flag-navy">Field Data</strong> — Lawn sign tracker + volunteer database.</div>
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 rounded-md bg-flag-navy text-white items-center justify-center"><ClipboardList className="h-4 w-4" /></span>
                <div><strong className="text-flag-navy">Planning</strong> — Campaign calendar + launch checklist.</div>
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 rounded-md bg-flag-red text-white items-center justify-center"><UsersIcon className="h-4 w-4" /></span>
                <div><strong className="text-flag-navy">Voter List</strong> <span className="text-[10px] font-bold uppercase tracking-wider bg-flag-red text-white rounded-full px-1.5 py-0.5 ml-1">Pro</span> — Upload, tag, filter your voter file.</div>
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 rounded-md bg-flag-navy text-white items-center justify-center"><Wallet className="h-4 w-4" /></span>
                <div><strong className="text-flag-navy">Finances</strong> <span className="text-[10px] font-bold uppercase tracking-wider bg-flag-red text-white rounded-full px-1.5 py-0.5 ml-1">Pro</span> — Fundraising, budget, reports, CSV export.</div>
              </li>
            </ul>
          </>
        ),
      };
    }
    return {
      eyebrow: "Last thing",
      title: "Let's get your profile filled out.",
      body: (
        <>
          <p>Once your profile is in, everything else in here gets dramatically more useful in about 30 seconds. That's it. We&rsquo;ll point you at the right next step from there.</p>
          <p className="mt-3 text-sm text-slate-500">You can always come back to this tour from <strong>Settings → Help</strong>.</p>
        </>
      ),
    };
  })();

  const isLast = step === totalSteps - 1;
  const isFirst = step === 0;

  const onPrimary = () => {
    if (isLast) {
      onComplete();
      navigate("/campaign-intake");
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-2xl p-0 overflow-hidden"
        data-testid="onboarding-dialog"
        onInteractOutside={(e) => e.preventDefault()}  // don't dismiss on backdrop click
      >
        <div className="bg-flag-navy text-white px-6 sm:px-8 py-4 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em]">
            <Sparkles className="h-3.5 w-3.5 text-flag-red" />
            {stepBody.eyebrow}
          </div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-300">{step + 1} of {totalSteps}</div>
        </div>

        <div className="px-6 sm:px-8 pt-6 max-h-[70vh] overflow-y-auto">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl sm:text-3xl font-black text-flag-navy leading-tight" data-testid="onboarding-step-title">
              {stepBody.title}
            </DialogTitle>
            <DialogDescription className="sr-only">Onboarding step {step + 1} of {totalSteps}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 text-slate-700 leading-relaxed text-[15px]" data-testid="onboarding-step-body">
            {stepBody.body}
          </div>
        </div>

        <DialogFooter className="px-6 sm:px-8 py-4 mt-2 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row-reverse gap-2 sm:items-center sm:justify-between">
          <div className="flex gap-2 w-full sm:w-auto">
            {!isFirst && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="onboarding-back"
                className="border-2 font-semibold h-10"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            <Button
              type="button"
              onClick={onPrimary}
              data-testid="onboarding-next"
              className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-10 px-5 flex-1 sm:flex-none"
            >
              {isLast ? (
                <>Start my profile <ClipboardCheck className="h-4 w-4 ml-1.5" /></>
              ) : (
                <>Next <ArrowRight className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="onboarding-skip"
            className="text-sm text-slate-500 hover:text-slate-800 font-semibold underline-offset-2 hover:underline self-center sm:self-auto"
          >
            Skip for now
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Top-level controller. Mount once in the app shell. Auto-opens for new
 * users on the dashboard, and also exposes a global helper via context
 * for the "Take the tour again" button in Settings.
 */
export default function OnboardingTour() {
  const { user, reload } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  // Auto-open: only on dashboard, only if not yet onboarded, only once per session.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (user.onboarded_at) return;
    if (dismissedThisSession) return;
    if (!ROUTES_WHERE_AUTO_OPENS.has(location.pathname)) return;
    setOpen(true);
  }, [user, location.pathname, dismissedThisSession]);

  // Re-opening manually from Settings: a custom event triggers it.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("electable:replay-onboarding", handler);
    return () => window.removeEventListener("electable:replay-onboarding", handler);
  }, []);

  const markServerDone = async () => {
    try {
      await api.post("/auth/onboarding/complete");
      reload();  // refresh user so onboarded_at sticks in context
    } catch {
      // Best-effort: if the call fails, the local dismissal still hides the
      // modal for this session. They'd just see it again next time, which is
      // acceptable.
    }
  };

  const close = () => {
    setOpen(false);
    setDismissedThisSession(true);
    if (user && !user.onboarded_at) markServerDone();
  };

  const complete = () => {
    setOpen(false);
    setDismissedThisSession(true);
    if (user && !user.onboarded_at) markServerDone();
  };

  return <OnboardingDialog open={open} onClose={close} onComplete={complete} />;
}

/**
 * Re-usable button that re-opens the welcome tour. Drop it in Settings,
 * a "Help" menu, or anywhere else. Doesn't reset the server-side flag —
 * the tour just shows again on demand.
 */
export function OnboardingReplayButton({ className = "", testid = "onboarding-replay-btn", children = "Take the welcome tour again" }) {
  const onClick = () => window.dispatchEvent(new CustomEvent("electable:replay-onboarding"));
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={className}>
      {children}
    </button>
  );
}
